import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BarChart3, Users, CalendarDays, CalendarCheck, LogIn, ListChecks, UserX, MapPin, MessageSquare } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { comeConosciutoLabel, esitoLabel, esitoColor } from "../config/ingressi";

const todayIso = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const periodRange = (p) => {
  const now = new Date();
  if (p === "settimana") { const s = new Date(now); s.setDate(now.getDate() - 6); return [iso(s), todayIso()]; }
  if (p === "mese") { return [iso(new Date(now.getFullYear(), now.getMonth(), 1)), todayIso()]; }
  if (p === "trimestre") { const s = new Date(now); s.setMonth(now.getMonth() - 2, 1); return [iso(s), todayIso()]; }
  if (p === "anno") { return [iso(new Date(now.getFullYear(), 0, 1)), todayIso()]; }
  return [null, null];
};

const PERIODS = [["settimana", "Settimana"], ["mese", "Mese"], ["trimestre", "Trimestre"], ["anno", "Anno"], ["custom", "Personalizzato"]];

const Stat = ({ icon: Icon, label, value, color }) => (
  <div className="gr-card p-4 flex items-center gap-3" data-testid={`report-stat-${label}`}>
    <span className="h-10 w-10 rounded-lg grid place-items-center shrink-0" style={{ background: color + "1a", color }}><Icon size={18} /></span>
    <div><div className="text-2xl font-head font-bold tabnum">{value}</div><div className="text-xs text-muted-foreground">{label}</div></div>
  </div>
);

const Rate = ({ label, value }) => (
  <div className="gr-card p-4 text-center" data-testid={`report-rate-${label}`}>
    <div className="text-2xl font-head font-bold text-primary tabnum">{value}%</div>
    <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
  </div>
);

export default function ReportAmmissioni() {
  const { selectedStructure } = useAuth();
  const [period, setPeriod] = useState("mese");
  const [custom, setCustom] = useState({ da: "", a: "" });

  const [da, a] = useMemo(() => (period === "custom" ? [custom.da || null, custom.a || null] : periodRange(period)), [period, custom]);

  const params = { ...(da ? { da } : {}), ...(a ? { a } : {}), ...(selectedStructure && selectedStructure !== "all" ? { structure_id: selectedStructure } : {}) };
  const { data = null } = useQuery({
    queryKey: ["ingressi-report", da, a, selectedStructure],
    queryFn: async () => (await api.get("/ingressi/report", { params })).data,
  });

  const r = data || { nuovi_contatti: 0, visite_programmate: 0, visite_effettuate: 0, ingressi: 0, lista_attesa: 0, rinunce: 0, tasso_contatto_visita: 0, tasso_visita_ingresso: 0, tasso_contatto_ingresso: 0, provenienza: [], provenienza_geo: [], feedbacks: [] };

  return (
    <div data-testid="report-ammissioni-page">
      <div className="mb-5">
        <h1 className="text-2xl font-head font-bold flex items-center gap-2"><BarChart3 size={22} className="text-primary" /> Report Ammissioni</h1>
        <p className="text-sm text-muted-foreground">Statistiche contatti, visite e ingressi per periodo</p>
      </div>

      <div className="flex items-center gap-2 mb-5 flex-wrap">
        <div className="flex rounded-lg border overflow-hidden" data-testid="report-period">
          {PERIODS.map(([k, l]) => (
            <button key={k} onClick={() => setPeriod(k)} data-testid={`report-period-${k}`}
              className={`px-3 h-9 text-sm font-semibold transition-colors ${period === k ? "bg-primary text-white" : "hover:bg-secondary"}`}>{l}</button>
          ))}
        </div>
        {period === "custom" && (
          <div className="flex items-center gap-2">
            <input type="date" value={custom.da} onChange={(e) => setCustom((p) => ({ ...p, da: e.target.value }))} className="h-9 rounded-md border px-2 text-sm" data-testid="report-da" />
            <span className="text-muted-foreground text-sm">→</span>
            <input type="date" value={custom.a} onChange={(e) => setCustom((p) => ({ ...p, a: e.target.value }))} className="h-9 rounded-md border px-2 text-sm" data-testid="report-a" />
          </div>
        )}
      </div>

      <div className="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-6 mb-6">
        <Stat icon={Users} label="Nuovi contatti" value={r.nuovi_contatti} color="#2563eb" />
        <Stat icon={CalendarDays} label="Visite programmate" value={r.visite_programmate} color="#7c3aed" />
        <Stat icon={CalendarCheck} label="Visite effettuate" value={r.visite_effettuate} color="#16a34a" />
        <Stat icon={LogIn} label="Ingressi" value={r.ingressi} color="#059669" />
        <Stat icon={ListChecks} label="In lista d'attesa" value={r.lista_attesa} color="#c026d3" />
        <Stat icon={UserX} label="Rinunce" value={r.rinunce} color="#dc2626" />
      </div>

      <h2 className="font-head font-bold mb-2">Tassi di conversione</h2>
      <div className="grid gap-3 grid-cols-1 sm:grid-cols-3 mb-6">
        <Rate label="Contatto → Visita" value={r.tasso_contatto_visita} />
        <Rate label="Visita → Ingresso" value={r.tasso_visita_ingresso} />
        <Rate label="Contatto → Ingresso" value={r.tasso_contatto_ingresso} />
      </div>

      <h2 className="font-head font-bold mb-2">Analisi provenienza</h2>
      <p className="text-xs text-muted-foreground mb-3">Quali canali portano più contatti, visite e ingressi</p>
      {(r.provenienza || []).length === 0 ? (
        <div className="gr-card p-8 text-center text-muted-foreground" data-testid="report-provenienza-empty">Nessun dato nel periodo selezionato.</div>
      ) : (
        <div className="gr-card overflow-x-auto" data-testid="report-provenienza">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr><th className="p-3">Canale</th><th className="p-3 text-center">Contatti</th><th className="p-3 text-center">Visite</th><th className="p-3 text-center">Ingressi</th><th className="p-3">Funnel</th></tr>
            </thead>
            <tbody>
              {r.provenienza.map((c) => (
                <tr key={c.canale} data-testid={`report-prov-${c.canale}`} className="border-t">
                  <td className="p-3 font-semibold">{comeConosciutoLabel(c.canale)}</td>
                  <td className="p-3 text-center tabnum font-bold text-blue-600">{c.contatti}</td>
                  <td className="p-3 text-center tabnum font-bold text-violet-600">{c.visite}</td>
                  <td className="p-3 text-center tabnum font-bold text-emerald-600">{c.ingressi}</td>
                  <td className="p-3 text-xs text-muted-foreground">{c.contatti} contatti → {c.visite} visite → {c.ingressi} ingressi</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h2 className="font-head font-bold mb-2 mt-6 flex items-center gap-2"><MapPin size={18} className="text-primary" /> Provenienza geografica</h2>
      <p className="text-xs text-muted-foreground mb-3">Da quali città / regioni arrivano più richieste e ingressi</p>
      {(r.provenienza_geo || []).length === 0 ? (
        <div className="gr-card p-8 text-center text-muted-foreground" data-testid="report-geo-empty">Nessun dato geografico nel periodo selezionato.</div>
      ) : (
        <div className="gr-card overflow-x-auto" data-testid="report-geo">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr><th className="p-3">Città / Regione</th><th className="p-3 text-center">Contatti</th><th className="p-3 text-center">Visite</th><th className="p-3 text-center">Ingressi</th><th className="p-3">Funnel</th></tr>
            </thead>
            <tbody>
              {r.provenienza_geo.map((c) => (
                <tr key={c.citta} data-testid={`report-geo-${c.citta}`} className="border-t">
                  <td className="p-3 font-semibold">{c.citta}</td>
                  <td className="p-3 text-center tabnum font-bold text-blue-600">{c.contatti}</td>
                  <td className="p-3 text-center tabnum font-bold text-violet-600">{c.visite}</td>
                  <td className="p-3 text-center tabnum font-bold text-emerald-600">{c.ingressi}</td>
                  <td className="p-3 text-xs text-muted-foreground">{c.contatti} contatti → {c.visite} visite → {c.ingressi} ingressi</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h2 className="font-head font-bold mb-2 mt-6 flex items-center gap-2"><MessageSquare size={18} className="text-primary" /> Feedback delle visite</h2>
      <p className="text-xs text-muted-foreground mb-3">Cosa hanno detto le famiglie in visita — utile per capire motivi ricorrenti di scelta o rinuncia</p>
      {(r.feedbacks || []).length === 0 ? (
        <div className="gr-card p-8 text-center text-muted-foreground" data-testid="report-feedback-empty">Nessun feedback registrato nel periodo selezionato.</div>
      ) : (
        <div className="space-y-2" data-testid="report-feedbacks">
          {r.feedbacks.map((f) => (
            <div key={f.id} data-testid={`report-feedback-${f.id}`} className="gr-card p-3 flex items-start gap-3">
              <MessageSquare size={16} className="text-muted-foreground mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm">{f.feedback}</div>
                <div className="text-[11px] text-muted-foreground mt-1">{f.ospite_nome}{f.data ? ` · ${f.data}` : ""}</div>
              </div>
              {f.esito && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0" style={{ background: esitoColor(f.esito) + "22", color: esitoColor(f.esito) }}>{esitoLabel(f.esito)}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
